import React, { useState, forwardRef, useEffect, useImperativeHandle, useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import axios from 'axios';
import styled from 'styled-components'
import { Input, Table, Button } from 'antd';
import { showModal } from '../../../store/actions';
import ContextMenu from '../../ContextMenu';
import { useTableSelectionState }  from '../../../hooks'
import _ from 'lodash';

const UserGrid = forwardRef(({size, contextMenu, onUserIdClick, onSelect, rootScope, location, isAdmin, onContextClick, groupFilter}, ref) => {

  const keys = useMemo(() => ['id', 'inWorkflow'], []);

  const [loading, setLoading] = useState(true);
  const [gridData, setGridData] = useState([]);
  const [pagination, setPagination] = useState({total: 0});
  const [currentFilters, setCurrentFilters] = useState({});
  const [menuCoordinates, setMenuCoordinates] = useState({});
  const [selectedUsers, setSelectedUsers] = useTableSelectionState(keys, []);

  let searchInput = null;

  useImperativeHandle(ref, () => ({
    refresh(resetPage = true) {
      loadUserGrid(currentFilters, resetPage);
    },
    getSelectedUsers() {
      return selectedUsers
    },
    getSelectedUserIds() {
      return selectedUsers.map(o => o.id)
    },
    clearSelectedUsers() {
      setSelectedUsers([])
    }
  }));

  useEffect(() => {
    _.isFunction(onSelect) && onSelect(selectedUsers);
  }, [onSelect, selectedUsers])

  const loadUserGrid = useCallback((options, resetPage) => {
    if(resetPage) options.skip = 0;
    setLoading(true);
      axios.post('/api/wt/userman/search/grid', { ...options, excludeGroup: groupFilter}).then(
      res => { 
        setGridData(res.data.data);
        setPagination(p => ({ ...p, current: resetPage ? 1 : p.current, total: res.data._meta.totalRecords }));
        setLoading(false)
      }
    ).catch(error => {
      console.error(error);
      alert('Error loading data.');
      setLoading(false);      
    });
  }, [groupFilter]);

  const cssadminSelected = useMemo(() => {
    return selectedUsers.length === 1 && selectedUsers[0]?.id === 'cssadmin';
  }, [selectedUsers])

  useEffect(() => {
    const filters = { limit: 50, skip: 0};

    setCurrentFilters(filters);
    loadUserGrid(filters, false);

  }, [loadUserGrid])

  const getColumnSearchProps = (columnTitle, dataIndex) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={node => {
            searchInput = node;
          }}
          placeholder={`Filter ${columnTitle}`}
          value={selectedKeys[0]}
          onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(dataIndex, selectedKeys, confirm)}
          style={{ width: 188, marginBottom: 8, display: 'block' }}
        />
        <Button
          type="primary"
          onClick={() => handleSearch(dataIndex, selectedKeys, confirm)}
          style={{ width: 90, marginRight: 8 }}
        >
          Apply
        </Button>
        <Button onClick={() => handleReset(clearFilters, confirm)} size="small" style={{ width: 90 }}>
          Reset
        </Button>
      </div>
    ),
    onFilterDropdownOpenChange: visible => {
      if (visible) {
        setTimeout(() => searchInput.select());
      }
    },
  });

  const handleSearch = (dataIndex, selectedKeys, confirm) => {
    confirm();
  };

  const handleReset = (clearFilters, confirm) => {
    clearFilters();
    confirm();
  };

  const handleTableChange = (pagination, filters, sorter) => {

    const pager = { ...pagination };
    pager.current = pagination.current;
    setPagination(pager);
        
    const filterObject = {};

    _.forEach(filters, (value, field) => {
      if(value) filterObject[field] = value[0]
    })

    const cf = {
      limit: pagination.pageSize,
      skip: (pagination.current - 1) * pagination.pageSize,
      order: [{
        column: sorter.columnKey,
        direction: sorter.order === 'ascend' ? 0 : 1
      }],
      userSqlFilter: filterObject
    }

    setCurrentFilters(cf)
    loadUserGrid(cf, false);
  };

  const columns = [
    {
      key: 'ID',
      title: 'User ID',
      dataIndex: 'id',
      ellipsis: true,
      defaultSortOrder: 'ascend',
      sorter: true,
      render: (value) => (
        _.isFunction(onUserIdClick) ?
          <a onClick={onUserIdClick}>
            {value}
          </a>
          : <span>{value}</span>
      ),
      ...getColumnSearchProps('User ID', 'ID'),
    },
    {
      key: 'FirstName',
      title: 'First Name',
      dataIndex: 'firstName',
      sorter: true,
      ellipsis: true,
      ...getColumnSearchProps('First Name', 'FirstName'),
    },
    {
      key: 'LastName',
      title: 'Last Name',
      dataIndex: 'lastName',
      sorter: true,
      ellipsis: true,
      ...getColumnSearchProps('Last Name', 'LastName'),
    },
    {
      key: 'Department',
      title: 'Department',
      dataIndex: 'department',
      sorter: true,
      ellipsis: true,
      ...getColumnSearchProps('Department', 'Department'),
    },
    {
      key: 'Title',
      title: 'Title',
      dataIndex: 'title',
      sorter: true,
      ellipsis: true,
      ...getColumnSearchProps('Title', 'Title'),
    },
    {
      key: 'Supervisor',
      title: 'Manager',
      dataIndex: 'manager',
      sorter: true,
      ellipsis: true,
      ...getColumnSearchProps('Manager', 'Manager'),
    },
  ];

  const rowSelection = {
    selectedRowKeys: selectedUsers.map(o => o.id),
    onSelect: (selectedKey, selected) => {
      setSelectedUsers(selected, false, [selectedKey]);
    },
    onSelectMultiple: (selected, selectedRows) => {
      setSelectedUsers(selected, false, selectedRows);
    },
    onSelectAll: (selected, selectedRows) => {
      setSelectedUsers(selected, false, !selected ? gridData : selectedRows);
    }
  };

  const onRowClick = (event, record) => {
    if (event.target.className.indexOf('ant-checkbox') > -1) return;
    setSelectedUsers(true, true, [record]);
  }

  const openContextMenu = (e, record) => {

    _.isFunction(onSelect) && onSelect(selectedUsers);

    const isSelected = e.target.closest('tr').className.indexOf('ant-table-row-selected') > -1;

    e.preventDefault();

    if(!isAdmin) return;

    setSelectedUsers(true, !isSelected, [record]);
    
    let coordinates = { x: e.pageX - 50, y: e.pageY - 50 };

    setMenuCoordinates(coordinates);
  }

  return(
    <>
    <FlexContainer>
      <TableContainer>
        <Table
          scroll={{scrollToFirstRowOnChange: true, y: '400px'}}
          tableLayout="fixed"
          size={size}
          loading={loading}
          showSorterTooltip={false}
          pagination={{
            total: pagination.total,
            current: pagination.current,
            showSizeChanger: false,
            defaultPageSize: 50,
            defaultCurrent: 1
          }}
          rowSelection={{
            type: 'checkbox',
            ...rowSelection,
          }}
          columns={columns}
          dataSource={gridData}
          rowKey="id"
          onChange={handleTableChange}
          onRow={(record, rowIndex) => {
            return {
              onClick: event => onRowClick(event, record),
              onContextMenu: event => openContextMenu(event, record)
            };
          }}
        />
        </TableContainer>
    </FlexContainer>
    {isAdmin && !cssadminSelected &&
      <ContextMenu
        menuItems={contextMenu}
        coordinates={menuCoordinates}
        handleClose={() => setMenuCoordinates({})}
        onClick={onContextClick}
      />
    }
  </>
  )
})

const FlexContainer = styled.div`
  display: flex;
`
const TableContainer = styled.div`
  flex: auto;
  min-width: 300px;
  .ant-table-body,
  .ant-table-tbody,
  .ant-table-content,
  .ant-table-placeholder,
  .ant-table-expanded-row-fixed {
    overflow: auto !important;
  }
  .ant-pagination {
    float: left;
    margin: 6px 0 0 15px;
  }
  .ant-table-cell:has(.ant-empty-normal) {
    border-bottom: none !important;
  }
`
const mapStateToProps = state => {
  const { isAdmin } = state.user;
  return { isAdmin };
};

const mapDispatchToProps = { showModal };

export default connect(mapStateToProps, mapDispatchToProps, null, {forwardRef: true})(UserGrid);